import React, { useContext, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import MetisMenu from "metismenujs"
import PropTypes from "prop-types"
import SimpleBar from "simplebar-react"
import { withRouter } from "react-router-dom"
import { withTranslation } from "react-i18next"
import Skeleton from "react-loading-skeleton"
import useFeatureFlags from "hooks/useFeatureFlags"
import { ADMIN_USER_PERMISSIONS, NAME_OF_PRODUCT_TYPES, REQUIRED_FEATURE_FLAGS } from "helpers/sf_constants"
import _ from "lodash"
import { AppContext, getPrimaryTextClass, isAdminAuthorized } from "helpers/sf_utils"
import { bsh_getAdminUserDetails, bsh_getInsuranceIoUserDetails } from "helpers/sf_browser_storage_helper"
import { isInsuranceioBrand } from "config"
import RecentlyVisited from "./RecentlyVisited"
import { canSeeMilo } from "pages/Admin/Milo/milo_helper"

const AdminSidebarContent = props => {
    const { isSidebarMinimized, setIsSidebarMinimized } = useContext(AppContext);
    const ref = useRef()
    const [isFeatureFlagActive] = useFeatureFlags();

    // Hooks must run before the loading early-return below (rules of hooks). The prior order ran
    // the early-return first, so the hook count changed once data loaded and crashed the shell.
    const isSidebarLoading = props?.loadings || !props.currentAdminUser || !props?.adminRolePermissions;

    useEffect(() => {
        if (isSidebarLoading) return;
        const pathName = props.location.pathname

        const initMenu = () => {
            new MetisMenu("#side-menu")
            let matchingMenuItem = null
            const ul = document.getElementById("side-menu")
            const items = ul.getElementsByTagName("a")
            for (let i = 0; i < items.length; ++i) {
                if (pathName.includes(items[i].pathname)) {
                    matchingMenuItem = items[i]
                    break
                }
            }
            if (matchingMenuItem) {
                activateParentDropdown(matchingMenuItem)
            }
        }
        initMenu()
    }, [props.location.pathname, isSidebarLoading])

    useEffect(() => {
        // ref is unset while the skeleton shows.
        if (ref.current) ref.current.recalculate()
    })

    if (isSidebarLoading) {
        return (
            <div id="sidebar-menu">
                <ul className="metismenu list-unstyled ms-2" id="side-menu">
                    <li className="my-4 px-3">
                        <Skeleton baseColor="#494E5E" highlightColor="#444" />
                    </li>
                    <li className="my-4 px-3">
                        <Skeleton baseColor="#494E5E" highlightColor="#444" />
                    </li>
                    <li className="my-4 px-3">
                        <Skeleton baseColor="#494E5E" highlightColor="#444" />
                    </li>
                    <li className="my-4 px-3">
                        <Skeleton baseColor="#494E5E" highlightColor="#444" />
                    </li>
                    <li className="my-4 px-3">
                        <Skeleton baseColor="#494E5E" highlightColor="#444" />
                    </li>
                    <li className="my-4 px-3">
                        <Skeleton baseColor="#494E5E" highlightColor="#444" />
                    </li>
                    <li className="my-4 px-3">
                        <Skeleton baseColor="#494E5E" highlightColor="#444" />
                    </li>
                    <li className="my-4 px-3">
                        <Skeleton baseColor="#494E5E" highlightColor="#444" />
                    </li>
                </ul>
            </div>
        )
    }

    function scrollElement(item) {
        if (item) {
            const currentPosition = item.offsetTop
            if (currentPosition > window.innerHeight) {
                ref.current.getScrollElement().scrollTop = currentPosition - 300
            }
        }
    }

    function activateParentDropdown(item) {
        item.classList.add("active")
        const parent = item.parentElement
        const parent2El = parent.childNodes[1]
        if (parent2El && parent2El.id !== "side-menu") {
            parent2El.classList.add("mm-show")
        }

        if (parent) {
            parent.classList.add("mm-active")
            const parent2 = parent.parentElement

            if (parent2) {
                parent2.classList.add("mm-show") // ul tag

                const parent3 = parent2.parentElement // li tag

                if (parent3) {
                    parent3.classList.add("mm-active") // li
                    parent3.childNodes[0].classList.add("mm-active") //a
                    const parent4 = parent3.parentElement // ul
                    if (parent4) {
                        parent4.classList.add("mm-show") // ul
                        const parent5 = parent4.parentElement
                        if (parent5) {
                            parent5.classList.add("mm-show") // li
                            parent5.childNodes[0].classList.add("mm-active") // a tag
                        }
                    }
                }
            }
            scrollElement(item)
            return false
        }
        scrollElement(item)
        return false
    }

    const insurance_io_user_token = bsh_getInsuranceIoUserDetails()?.token;
    const admin_user_token = bsh_getAdminUserDetails()?.token;

    return (
        <React.Fragment>
            <SimpleBar className="h-100" ref={ref}>
                <div id="sidebar-menu">
                    <ul className="metismenu list-unstyled ms-2" id="side-menu">
                        <>
                            {
                                (!props.adminRolePermissions || isAdminAuthorized('/dashboard', props.adminRolePermissions)) &&
                                isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_DASHBOARD_FOR_ADMIN_PORTAL) &&
                                <li>
                                    <Link to="/dashboard">
                                        <>
                                            <i className={`${getPrimaryTextClass()} bx bx-rocket`} />
                                            <span>{props.t("Overview")}</span>
                                        </>
                                    </Link>
                                </li>
                            }
                            {
                                (!props.adminRolePermissions || isAdminAuthorized('/client-users', props.adminRolePermissions)) &&
                                <li>
                                    <Link to="/client-users">
                                        <>
                                            <i className={`${getPrimaryTextClass()} bx bx-group`} />
                                            <span>{props.t("Clients")}</span>
                                        </>
                                    </Link>
                                </li>
                            }
                            {
                                (!props.adminRolePermissions || !!_.intersection(props.adminRolePermissions, [
                                    ADMIN_USER_PERMISSIONS.MANAGE_ADMIN_ROLES,
                                    ADMIN_USER_PERMISSIONS.MANAGE_ADMIN_USERS,
                                    ADMIN_USER_PERMISSIONS.MANAGE_ASSIGNED_ADMIN_USERS,
                                    ADMIN_USER_PERMISSIONS.VIEW_ADMIN_USERS,
                                    ADMIN_USER_PERMISSIONS.VIEW_ASSIGNED_ADMIN_USERS
                                ]).length) &&
                                <li>
                                    <Link to="/client-users" className={true ? "has-arrow" : ""}>
                                        <> <i className={`${getPrimaryTextClass()} bx bx-user`} />
                                            <span>{props.t("Organization")}</span>
                                        </>
                                    </Link>
                                    <ul aria-expanded="false">
                                        {
                                            (!props.adminRolePermissions || isAdminAuthorized('/admin_internal_user', props.adminRolePermissions)) &&
                                            <li>
                                                <Link to="/admin_internal_user">
                                                    <span className="d-flex justify-content-between align-items-center">
                                                        <i className={`${getPrimaryTextClass()} bx bx-user-pin`} />
                                                        <span>{props.t("User Management")}</span>
                                                    </span>
                                                </Link>
                                            </li>
                                        }
                                        {
                                            (!props.adminRolePermissions || isAdminAuthorized('/admin_user_roles', props.adminRolePermissions)) &&
                                            <li>
                                                <Link to="/admin_user_roles">
                                                    <>
                                                        <i className={`${getPrimaryTextClass()} bx bx-book-content`} />
                                                        <span>{props.t("Roles")}</span>
                                                    </>
                                                </Link>
                                            </li>
                                        }
                                        {
                                            (!props.adminRolePermissions || isAdminAuthorized('/admin_notification_templates', props.adminRolePermissions)) &&
                                            isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_ADMIN_ACCOUNT_NOTIFICATIONS) &&
                                            <li>
                                                <Link to="/admin_notification_templates">
                                                    <span className="d-flex justify-content-between align-items-center">
                                                        <i className={`${getPrimaryTextClass()} bx bx-notification`} />
                                                        <span>{props.t("Notification Templates")}</span>
                                                    </span>
                                                </Link>
                                            </li>
                                        }
                                    </ul>
                                </li>
                            }

                            {(!props.adminRolePermissions || isAdminAuthorized('/accounting', props.adminRolePermissions)) &&
                                isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_PAY_INVOICE_PAGE) &&
                                <li>
                                    <Link to="/client-users" className={props?.adminRolePermissions ? "has-arrow" : ""}>
                                        <> <i className={`${getPrimaryTextClass()} bx bx-book`} />
                                            <span>{props.t("Accounting")}</span>
                                        </>
                                    </Link>
                                    <ul aria-expanded="false">
                                        <li>
                                            <Link to="/accounting/invoices">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-dollar-circle`} />
                                                    <span>{props.t("Invoices")}</span>
                                                </>
                                            </Link>
                                        </li>

                                        <li hidden={!isFeatureFlagActive(REQUIRED_FEATURE_FLAGS?.SF_FEATURE_FLAG_SHOW_RECEIPT_IN_ADMIN_PORTAL)}>
                                            <Link to="/accounting/receipts">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} fa fa-coins`} />
                                                    <span>{props.t("Receipts")}</span>
                                                </>
                                            </Link>
                                        </li>

                                        <li hidden={!isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_RECEIPT_TYPE_IN_ADMIN_PORTAL)}>
                                            <Link to="/accounting/receipt_types">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-receipt`} />
                                                    <span>{props.t("Receipt Types")}</span>
                                                </>
                                            </Link>
                                        </li>

                                        <li hidden={!isFeatureFlagActive(REQUIRED_FEATURE_FLAGS?.SF_FEATURE_FLAG_SHOW_PAYMENT_TERMS)}>
                                            <Link to="/accounting/payment_terms">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-calendar-event`} />
                                                    <span>{props.t("Payment Terms")}</span>
                                                </>
                                            </Link>
                                        </li>

                                        <li hidden={!isFeatureFlagActive(REQUIRED_FEATURE_FLAGS?.SF_FEATURE_FLAG_SHOW_BILLING_EVENTS_PAGE)}>
                                            <Link to="/accounting/billing_events">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bxs-dollar-circle`} />
                                                    <span>{props.t("Billing Events")}</span>
                                                </>
                                            </Link>
                                        </li>
                                    </ul>
                                </li>
                            }

                            {!props.adminRolePermissions || (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_ADMIN_SF_FILTERS)) && isAdminAuthorized('/admin/clicks/sf_filters', props.adminRolePermissions) && (
                                <li>
                                    <Link to="/client-users" className={props?.adminRolePermissions ? "has-arrow" : ""}>
                                        <> <i className={`${getPrimaryTextClass()} bx bx-navigation`} />
                                            <span>{props.t("Clicks")}</span>
                                        </>
                                    </Link>
                                    <ul aria-expanded="false">
                                        <li>
                                            <Link to={`/admin/${NAME_OF_PRODUCT_TYPES.CLICKS}/sf_filters`}>
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-filter`} />
                                                    <span>{props.t("Click Filters")}</span>
                                                </>
                                            </Link>
                                        </li>
                                    </ul>
                                </li>
                            )}

                            <li>
                                <Link to="/client-users" className={props?.adminRolePermissions ? "has-arrow" : ""}>
                                    <> <i className={`${getPrimaryTextClass()} bx bx-conversation`} />
                                        <span>{props.t("Leads")}</span>
                                    </>
                                </Link>
                                <ul aria-expanded="false">
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_ADMIN_SF_FILTERS) && isAdminAuthorized('/admin/leads/sf_filters', props.adminRolePermissions) && (<li>
                                        <Link to={`/admin/${NAME_OF_PRODUCT_TYPES.LEADS}/sf_filters`}>
                                            <>
                                                <i className={`${getPrimaryTextClass()} bx bx-filter`} />
                                                <span>{props.t("Lead Filters")}</span>
                                            </>
                                        </Link>
                                    </li>)}
                                    {!props.adminRolePermissions || isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.FRONTEND_SHOW_ADMIN_LEAD_REFUND_REASONS) &&
                                        isAdminAuthorized('/refund_reasons', props.adminRolePermissions) &&
                                        <li>
                                            <Link to="/refund_reasons">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-reset`} />
                                                    <span>{props.t("Refund Reasons")}</span>
                                                </>
                                            </Link>
                                        </li>
                                    }
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_CALL_PRICES_IN_ADMIN_CALLS_PAGE) && (
                                        <li>
                                            <Link to="/admin_lead_prices">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-dollar`} />
                                                    <span>{props.t("Lead Prices")}</span>
                                                </>
                                            </Link>
                                        </li>)}
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_LEAD_REFUND_IN_ADMIN_LEADS_PAGE) && (
                                        <li>
                                            <Link to="/admin_lead_refunds">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-share`} />
                                                    <span>{props.t("Lead Refunds")}</span>
                                                </>
                                            </Link>
                                        </li>)
                                    }
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_FILTER_PACKAGES) && (isAdminAuthorized('/admin/leads/filter_packages', props.adminRolePermissions) || (insurance_io_user_token && (admin_user_token !== insurance_io_user_token))) && (

                                        <li>
                                            <Link to={`/admin/${NAME_OF_PRODUCT_TYPES.LEADS}/filter_packages`}>
                                                <span className="d-flex justify-content-between align-items-center">
                                                    <i className={`${getPrimaryTextClass()} bx bx-filter-alt`} />
                                                    <span>{props.t("Targeting Packages")}</span>
                                                </span>
                                            </Link>
                                        </li>)
                                    }
                                </ul>
                            </li>

                            <li>
                                <Link to="/client-users" className={props?.adminRolePermissions ? "has-arrow" : ""}>
                                    <> <i className={`${getPrimaryTextClass()} bx bx-phone-call`} />
                                        <span>{props.t("Calls")}</span>
                                    </>
                                </Link>
                                <ul aria-expanded="false">
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_ADMIN_SF_FILTERS) && (isAdminAuthorized('/admin/calls/sf_filters', props.adminRolePermissions) || (insurance_io_user_token && (admin_user_token !== insurance_io_user_token))) && (<li>
                                        <Link to={`/admin/${NAME_OF_PRODUCT_TYPES.CALLS}/sf_filters`}>
                                            <>
                                                <i className={`${getPrimaryTextClass()} bx bx-filter`} />
                                                <span>{props.t("Call Filters")}</span>
                                            </>
                                        </Link>
                                    </li>)}
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SHOW_ADMIN_TWILIO_NUMBER_UNDER_CALL) && (
                                        <li>
                                            <Link to="/twilio_numbers">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-dialpad-alt`} />
                                                    <span>{props.t("Twilio Numbers")}</span>
                                                </>
                                            </Link>
                                        </li>
                                    )}
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_CALL_PRICES_IN_ADMIN_CALLS_PAGE) && (
                                        <li>
                                            <Link to="/admin_call_prices">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-dollar`} />
                                                    <span>{props.t("Call Prices")}</span>
                                                </>
                                            </Link>
                                        </li>)}
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_FILTER_PACKAGES) && isAdminAuthorized('/admin/calls/filter_packages', props.adminRolePermissions) && (
                                        <li>
                                            <Link to={`/admin/${NAME_OF_PRODUCT_TYPES.CALLS}/filter_packages`}>
                                                <span className="d-flex justify-content-between align-items-center">
                                                    <i className={`${getPrimaryTextClass()} bx bx-filter-alt`} />
                                                    <span>{props.t("Targeting Packages")}</span>
                                                </span>
                                            </Link>
                                        </li>
                                    )}
                                </ul>
                            </li>
                            <li>
                                <Link to="/client-users" className={props?.adminRolePermissions ? "has-arrow" : ""}>
                                    <> <i className={`${getPrimaryTextClass()} bx bx-slider`} />
                                        <span>{props.t("Tools")}</span>
                                    </>
                                </Link>
                                <ul aria-expanded="false">
                                    <li>
                                        <Link to="/admin_profile">
                                            <>
                                                <i className={`${getPrimaryTextClass()} bx bx-wrench`} />
                                                <span>{props.t("Settings")}</span>
                                            </>
                                        </Link>
                                    </li>
                                    {
                                        (!props.adminRolePermissions || isAdminAuthorized('/feature-flags', props.adminRolePermissions)) &&
                                        <li>
                                            <Link to="/feature-flags">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-code`} />
                                                    <span>{props.t("Feature Flags")}</span>
                                                </>
                                            </Link>
                                        </li>
                                    }
                                    {!props.adminRolePermissions || (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS?.SF_FEATURE_FLAG_SHOW_INSURANCE_CARRIERS) &&
                                        isAdminAuthorized('/insurance_carriers', props.adminRolePermissions)) && (
                                            <li>
                                                <Link to="/insurance_carriers">
                                                    <>
                                                        <i className={`${getPrimaryTextClass()} bx bx-building-house`} />
                                                        <span>{props.t("Insurance Carriers")}</span>
                                                    </>
                                                </Link>
                                            </li>
                                        )
                                    }
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_NOTIFICATIONS_TERMINAL) &&
                                        <li>
                                            <Link to="/notifications_terminal">
                                                <span className="d-flex justify-content-between align-items-center">
                                                    <i className={`${getPrimaryTextClass()} bx bx-bell-plus`} />
                                                    <span>{props.t("Notifications Terminal")}</span>
                                                </span>
                                            </Link>
                                        </li>
                                    }
                                    {
                                        (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS?.SF_FEATURE_FLAG_SHOW_TERMS_OF_SERVICES)) && (
                                            <li>
                                                <Link to="/terms_of_services">
                                                    <>
                                                        <i className={`${getPrimaryTextClass()} bx bx-receipt`} />
                                                        <span>{props.t("Terms of Service")}</span>
                                                    </>
                                                </Link>
                                            </li>
                                        )
                                    }
                                    {
                                        (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_REFUND_POLICIES)) && (
                                            <li>
                                                <Link to="/refund_policies">
                                                    <>
                                                        <i className={`${getPrimaryTextClass()} bx bx-receipt`} />
                                                        <span>{props.t("Refund Policies")}</span>
                                                    </>
                                                </Link>
                                            </li>
                                        )
                                    }
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_WHITELISTED_BRANDS) &&
                                        <li>
                                            <Link to="/whitelabeled_brands">
                                                <span className="d-flex justify-content-between align-items-center">
                                                    <i className={`${getPrimaryTextClass()} bx bx-diamond`} />
                                                    <span>{props.t("Whitelabeled Brands")}</span>
                                                </span>
                                            </Link>
                                        </li>
                                    }
                                    {props?.adminNotifications && isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_ADMIN_ACCOUNT_NOTIFICATIONS) &&
                                        <li>
                                            <Link to="/admin_notifications">
                                                <span className="d-flex justify-content-between align-items-center">
                                                    <i className={`${getPrimaryTextClass()} bx bx-bell`} />
                                                    <span>{props.t("Account Notifications")}</span>
                                                </span>
                                            </Link>
                                        </li>
                                    }
                                    {
                                        isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_ADMIN_INTEGRATION_LOGS_PAGE) &&
                                        <li>
                                            <Link to="/admin_lead_integrations_logs">
                                                <span className="d-flex justify-content-between align-items-center">
                                                    <i className={`${getPrimaryTextClass()} bx bx-shape-circle`} />
                                                    <span>{props.t("Lead Integration Logs")}</span>
                                                </span>
                                            </Link>
                                        </li>
                                    }
                                    {
                                        isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_ADMIN_CAMPAIGN_FULFILLMENT_PAGE) &&
                                        <li>
                                            <Link to="/admin_campaign_fulfillment">
                                                <span className="d-flex justify-content-between align-items-center">
                                                    <i className={`${getPrimaryTextClass()} bx bxs-megaphone`} />
                                                    <span>{props.t("Campaign Fulfillment")}</span>
                                                </span>
                                            </Link>
                                        </li>
                                    }
                                    {
                                        isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_MILO_DASHBOARD) &&
                                        canSeeMilo(props.currentAdminUser) &&
                                        <li>
                                            <Link to="/milo">
                                                <span className="d-flex justify-content-between align-items-center">
                                                    <i className={`${getPrimaryTextClass()} bx bx-trending-up`} />
                                                    <span>{props.t("MILO")}</span>
                                                </span>
                                            </Link>
                                        </li>
                                    }
                                </ul>
                            </li>
                            <li>
                                <Link to="/client-users" className={props?.adminRolePermissions ? "has-arrow" : ""}>
                                    <> <i className={`${getPrimaryTextClass()} bx bx-analyse`} />
                                        <span>{props.t("Activity")}</span>
                                    </>
                                </Link>
                                <ul aria-expanded="false">
                                    {
                                        (!props.adminRolePermissions || isAdminAuthorized('/admin_change_history', props.adminRolePermissions)) &&
                                        <li>
                                            <Link to="/admin_change_history">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-time`} />
                                                    <span>{props.t("Change History")}</span>
                                                </>
                                            </Link>
                                        </li>
                                    }
                                    {!props.adminRolePermissions || (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_EMAIL_EVENTS) &&
                                        isAdminAuthorized('/email-activity', props.adminRolePermissions)) &&
                                        <li>
                                            <Link to="/email-activity">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-mail-send`} />
                                                    <span>{props.t("Email Activity")}</span>
                                                </>
                                            </Link>
                                        </li>
                                    }
                                    {(!props.adminRolePermissions || isAdminAuthorized('/report-exports', props.adminRolePermissions)) &&
                                        <li>
                                            <Link to="/report-exports">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-file`} />
                                                    <span>{props.t("Report Exports")}</span>
                                                </>
                                            </Link>
                                        </li>
                                    }
                                    {!props.adminRolePermissions || (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS?.SF_FEATURE_FLAG_SHOW_USER_ACTIVITY) &&
                                        isAdminAuthorized('/user_activity', props.adminRolePermissions)) && (
                                            <li>
                                                <Link to="/user_activity">
                                                    <>
                                                        <i className={`${getPrimaryTextClass()} bx bx-user-voice`} />
                                                        <span>{props.t("User Activity")}</span>
                                                    </>
                                                </Link>
                                            </li>
                                        )
                                    }
                                    {
                                        isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_BULK_EDIT_JOBS_PAGE) &&
                                        <li>
                                            <Link to="/admin_bulk_migration">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-select-multiple`} />
                                                    <span>{props.t("Bulk Edit Logs")}</span>
                                                </>
                                            </Link>
                                        </li>
                                    }
                                </ul>
                            </li>
                            {
                                (!props.adminRolePermissions || isAdminAuthorized('/dev-dashboard', props.adminRolePermissions) ||
                                    (insurance_io_user_token && (admin_user_token !== insurance_io_user_token))) &&
                                isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_DEVELOPER_DASHBOARD) &&
                                <li>
                                    <Link to="/admin-monitor-dashboard" className={props?.adminRolePermissions ? "has-arrow" : ""}>
                                        <>
                                            <i className={`${getPrimaryTextClass()} bx bx-data`} />
                                            <span>{props.t("Dev Monitor")}</span>
                                        </>
                                    </Link>
                                    <ul>
                                        <li>
                                            <Link to="/dev-dashboard">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-error-alt`} />
                                                    <span>{props.t("App Monitor")}</span>
                                                </>
                                            </Link>
                                        </li>
                                        {isInsuranceioBrand && <li>
                                            <Link to="/dms-dashboard">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-timer`} />
                                                    <span>{props.t("DMS Logs")}</span>
                                                </>
                                            </Link>
                                        </li>}
                                        <li>
                                            <Link to="/rules-dashboard">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-error-alt`} />
                                                    <span>{props.t("Validation Logs")}</span>
                                                </>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/status-monitor">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-sitemap`} />
                                                    <span>{props.t("Status Monitor")}</span>
                                                </>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/automation-tests">
                                                <>
                                                    <i className={`${getPrimaryTextClass()} bx bx-sitemap`} />
                                                    <span>{props.t("Automation Tests")}</span>
                                                </>
                                            </Link>
                                        </li>
                                        {
                                            isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_RDS_MONITOR_IN_DEV_MONITOR) &&
                                            <li>
                                                <Link to="/rds-monitor">
                                                    <>
                                                        <i className={`${getPrimaryTextClass()} bx bx-timer`} />
                                                        <span>{props.t("RDS Monitor")}</span>
                                                    </>
                                                </Link>
                                            </li>
                                        }
                                        {
                                            isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_DS_APP_IN_DEV_MONITOR) &&
                                            <li>
                                                <Link to="/data-science-dashboard">
                                                    <>
                                                        <i className={`${getPrimaryTextClass()} bx bx-line-chart`} />
                                                        <span>{props.t("Data Science")}</span>
                                                    </>
                                                </Link>
                                            </li>
                                        }
                                    </ul>
                                </li>
                            }
                        </>
                        {
                            isFeatureFlagActive(REQUIRED_FEATURE_FLAGS?.SF_FEATURE_FLAG_TO_SHOW_RECENT_VISIT) && !isSidebarMinimized &&
                            <RecentlyVisited />
                        }
                    </ul>
                </div>
            </SimpleBar>
        </React.Fragment>
    )
}

AdminSidebarContent.propTypes = {
    location: PropTypes.object,
    t: PropTypes.any,
    adminRolePermissions: PropTypes.array
}

export default withRouter(withTranslation()(AdminSidebarContent))
